import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useLanguage } from "~/hooks/useLanguage";
import { tags } from "~/translations/tags/tags";
import { cn } from "~/components/ui";
import Spinner from "./Spinner";

type TypeaheadProperties = {
  options: { name: string }[];
  filter: string;
  callback: (pick: string) => void;
  hasDefaultValue?: boolean;
  isLoading?: boolean;
  requests?: number;
  disabled?: boolean;
  className?: string;
  withSeperators?: boolean;
  textDirection?: string;
  position?: string;
  manulAdd?: boolean;
  ["data-cy"]?: string;
};

type MenuPos = { top: number; left: number; width: number };

const getMenuPosition = (input: HTMLElement | null): MenuPos | null => {
  if (!input) return null;
  const rect = input.getBoundingClientRect();
  const width = Math.max(rect.width, 180);
  const rtl = document.documentElement.getAttribute("dir") !== "ltr";
  const margin = 8;
  let left = rtl ? rect.right - width : rect.left;
  left = Math.min(Math.max(left, margin), window.innerWidth - width - margin);
  return { top: rect.bottom + 6, left, width };
};

const Typeahead = ({
  options,
  filter,
  callback,
  hasDefaultValue,
  isLoading,
  requests,
  disabled,
  className,
  withSeperators,
  textDirection,
  position = "relative",
  manulAdd = false,
  ...props
}: TypeaheadProperties) => {
  const { lang } = useLanguage();
  const [results, setResults] = useState<{ name: string }[]>([]);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  const numberOfOptionsDisplayed = useRef<number>(100);
  const typeaheadContainerRef = useRef<HTMLDivElement>(null);
  const typeaheadInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const isFocused = useRef<boolean>(false);
  const showMenu = results.length > 0 || !!isLoading;

  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuPos(null);
      return;
    }

    const updatePosition = () => {
      const next = getMenuPosition(typeaheadInputRef.current);
      if (!next) return;
      const rect = typeaheadInputRef.current?.getBoundingClientRect();
      if (
        rect &&
        (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0)
      ) {
        setResults([]);
        return;
      }
      setMenuPos(next);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showMenu, results.length, isLoading]);

  useEffect(() => {
    if (!showMenu) return;

    const clickOutsideElement = (event: MouseEvent): void => {
      const target = event.target as Node | null;
      if (typeaheadContainerRef.current?.contains(target)) return;
      if (resultsRef.current?.contains(target)) return;
      setResults([]);
      numberOfOptionsDisplayed.current = 100;
      isFocused.current = false;
    };

    document.addEventListener("mousedown", clickOutsideElement);
    return () => document.removeEventListener("mousedown", clickOutsideElement);
  }, [showMenu]);

  useEffect(() => {
    if (isLoading === false && isFocused.current)
      setResults(
        options.length > 0
          ? options.slice(0, numberOfOptionsDisplayed.current)
          : manulAdd
          ? [{ name: tags[lang].filter.notFoundAddManual }]
          : [{ name: tags[lang].filter.resultsNotFound }]
      );
  }, [isLoading, options, manulAdd, lang]);

  useEffect(() => {
    if (filter === "") {
      //TODO: revisit this and understand why this was here. it was causing an annoying behaviur with selection where when you remove delection you lose the focus
      //setResults([]);
      numberOfOptionsDisplayed.current = 100;
      // isFocused.current = false;
      //typeaheadInputRef.current?.blur();
    }
  }, [filter, isLoading, options]);

  const filterResults = (
    keyword: string,
    options: { name: string }[],
    limit: number,
    isFocus?: boolean
  ): { name: string }[] => {
    if (isFocus && hasDefaultValue) {
      const displayedOptions = options.slice(0, limit);
      if (options.length > limit)
        displayedOptions.push({
          name: tags[lang].filter.displayAdditionalOptions,
        });

      return displayedOptions;
    }

    const searchResults = [];
    for (let i = 0; i < options.length; i++) {
      const itemName = options[i].name.toLowerCase();
      const keywordName = keyword.toLowerCase();
      if (itemName.includes(keywordName)) {
        searchResults.push(options[i]);
      }
      if (limit === searchResults.length && i < options.length - 1) {
        searchResults.push({
          name: tags[lang].filter.displayAdditionalOptions,
        });
        break;
      }
    }
    if (keyword.length > 0 && searchResults.length === 0) {
      return manulAdd
        ? [{ name: tags[lang].filter.notFoundAddManual }]
        : [{ name: tags[lang].filter.resultsNotFound }];
    }
    return searchResults;
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const nameValue = e.target.value;
    callback(nameValue);
    setResults([]);
    if (
      nameValue.length >= 0 &&
      (!requests || (requests && isLoading !== undefined))
    ) {
      setResults(
        filterResults(nameValue, options, numberOfOptionsDisplayed.current)
      );
    } else if (isLoading === undefined) setResults([]);
  };

  const onNameSelected = (selectedName: string): void => {
    callback(selectedName);
    setResults([]);
  };

  const onResultClick = (result: { name: string }): void => {
    if (result.name === tags[lang].filter.displayAdditionalOptions) {
      numberOfOptionsDisplayed.current = numberOfOptionsDisplayed.current + 100;
      setResults(
        filterResults(filter, options, numberOfOptionsDisplayed.current)
      );
    } else if (result.name === tags[lang].filter.notFoundAddManual) {
      onNameSelected(filter);
      isFocused.current = false;
    } else if (result.name !== tags[lang].filter.resultsNotFound) {
      onNameSelected(result.name);
    }
  };

  const menuStyle: CSSProperties | undefined = menuPos
    ? {
        position: "fixed",
        top: menuPos.top,
        left: menuPos.left,
        minWidth: menuPos.width,
        maxWidth: Math.min(window.innerWidth - 16, 480),
        right: "auto",
      }
    : undefined;

  const resultsMenu =
    showMenu && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={resultsRef}
            style={menuStyle}
            className={cn(
              "z-popover h-fit max-h-[150px] overflow-y-auto rounded-md border border-border-subtle bg-surface shadow-md mobile:max-h-[125px]",
              !menuPos && "invisible",
              className
            )}
          >
            {isLoading ? (
              <div className="flex items-center justify-center p-2">
                <Spinner />
              </div>
            ) : (
              results.map((result: { name: string }, index: number) => (
                <Fragment key={`${result.name}-${index}`}>
                  <div
                    className={`cursor-pointer justify-around bg-surface p-2 text-${textDirection} font-bold hover:bg-secondary hover:text-white`}
                    onClick={() => onResultClick(result)}
                  >
                    {result.name}
                  </div>
                  {withSeperators && (
                    <hr className="mb-1 mt-1 h-[1px] w-full bg-secondary" />
                  )}
                </Fragment>
              ))
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={typeaheadContainerRef} className={`${position} h-fit w-full`}>
      <input
        className="w-full text-center focus:outline-none"
        onChange={onInputChange}
        value={filter}
        ref={typeaheadInputRef}
        onFocus={() => {
          setResults(
            filterResults(
              filter,
              options,
              numberOfOptionsDisplayed.current,
              true
            )
          );
          isFocused.current = true;
        }}
        disabled={disabled}
        data-cy={props["data-cy"]}
      />
      {resultsMenu}
    </div>
  );
};

export default Typeahead;
